package com.swetdot.swetgram.ui.chat

import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.StringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chat_day_fri
import swetgram.shared.generated.resources.chat_day_mon
import swetgram.shared.generated.resources.chat_day_sat
import swetgram.shared.generated.resources.chat_day_sun
import swetgram.shared.generated.resources.chat_day_thu
import swetgram.shared.generated.resources.chat_day_today
import swetgram.shared.generated.resources.chat_day_tue
import swetgram.shared.generated.resources.chat_day_wed
import swetgram.shared.generated.resources.chat_day_yesterday
import swetgram.shared.generated.resources.feed_age_hours
import swetgram.shared.generated.resources.feed_age_minutes
import swetgram.shared.generated.resources.feed_age_now
import swetgram.shared.generated.resources.value_passthrough
import kotlin.time.Instant

/**
 * Time formatting for the chat screen.
 *
 * Hand-rolled rather than delegated to a platform formatter because the result must be
 * identical on Android and iOS -- a timestamp that reads 09:05 on one and 9:05 AM on the other
 * is the kind of inconsistency that only surfaces once both apps are side by side.
 *
 * 24-hour throughout: the launch audience is Cyprus and Europe (EN + RU), where 24-hour is the
 * norm. A locale-aware switch belongs here later, not scattered through the UI.
 */
fun formatClockTime(instant: Instant, zone: TimeZone = TimeZone.currentSystemDefault()): String {
    val local = instant.toLocalDateTime(zone)
    return "${local.hour.pad()}:${local.minute.pad()}"
}

private fun Int.pad(): String = if (this < 10) "0$this" else toString()

/**
 * Which label a day gets: "Today", "Yesterday", a short weekday name, or an explicit date.
 *
 * Returns a structured value rather than a finished string so the relative labels can be localized
 * (EN + RU) without this function knowing about locales.
 */
sealed interface DayLabel {
    data object Today : DayLabel
    data object Yesterday : DayLabel

    /**
     * Inside the last week: «Пн», "Mon". Carries the day anyway, so a caller that CANNOT use a
     * weekday name still has the date to fall back on -- see [dayLabel].
     */
    data class Weekday(val day: LocalDay) : DayLabel

    data class Absolute(val day: LocalDay) : DayLabel
}

/**
 * THE WEEKDAY WINDOW, AND WHY IT STOPS WHERE IT STOPS.
 *
 * "Within the last week" is ambiguous and had to be decided rather than guessed. A weekday name is
 * a label with only seven values, so the moment the window is wide enough to reach a name that is
 * already spoken for, the label starts meaning two different dates and the reader has no way to
 * tell which. That is strictly worse than a date: a date is longer and duller and always right.
 *
 * So the window is the largest one in which every name it can print is unique. Today's name and
 * yesterday's are taken -- those days say «Сегодня» and «Вчера» instead -- which leaves the five
 * days from two to six ago, and those five weekday names are distinct from each other and from the
 * two that are spoken for. SEVEN DAYS AGO IS THE SAME NAME AS TODAY, which answers the question
 * directly: on a Monday, last Monday is a DATE, not «Пн».
 *
 * Six days ago borrows tomorrow's name, which costs nothing: nothing in this app labels a future
 * day as a weekday. See [dayLabel] on the future.
 */
private const val OLDEST_WEEKDAY = -6L
private const val NEWEST_WEEKDAY = -2L

/**
 * The ONE relative-date rule, for every surface that shows a day.
 *
 * MEASURED IN CALENDAR DAYS, NOT IN HOURS. `LocalDay.epochDay` differences two dates that have no
 * clock in them, so there is nothing for a DST change to shift. The previous version subtracted
 * 86400 seconds from [now] and asked which day that landed on, which is off by a day on the two
 * nights a year that are not 24 hours long -- it knew, and said so in a comment, and the comment
 * was the only thing holding it.
 *
 * THE FUTURE IS A DATE, DELIBERATELY. A device with a skewed clock, or a peer whose clock runs
 * ahead, produces a timestamp later than [now]. There is no word for it -- this app has «Сегодня»
 * and «Вчера» and nothing forward -- and a weekday name would be worse than useless: tomorrow
 * carries the same name as six days ago, which is inside the window above. So anything after today
 * falls to an explicit date, the one form that cannot be misread. The old rule reached the same
 * output by falling through both equality checks; this reaches it on purpose and is tested for it.
 *
 * ONE RULE, NO OPT-OUT PARAMETER, AND THAT IS A CORRECTION. This function briefly took
 * `relativeWeekdays: Boolean = true` so the three surfaces that print a SENTENCE with the day inside
 * it -- both last-seen lines and the mute status line -- could keep saying a date. The argument was
 * INERT: each of those callers already had a `DayLabel.Weekday ->` branch printing `day.render()`,
 * byte-identical to its `Absolute` branch, so the parameter and the branch cancelled out. Deleting
 * all three arguments changed nothing and failed no test, which is how it was found.
 *
 * It is gone rather than documented, because a parameter that reads as a constraint and enforces
 * nothing is worse than no parameter: the next reader trusts it. The real opt-out is the branch, it
 * is ordinary code, and it runs.
 *
 * The reason those three surfaces want a date is unchanged and worth keeping here. The weekday names
 * are ABBREVIATIONS -- «Пн», "Mon" -- which stand alone in a column perfectly well and cannot be
 * dropped into a sentence: Russian would need «в понедельник», and «Последний раз в сети Пн» is not
 * a sentence. So a caller that prints a bare label takes [DayLabel.Weekday] as it comes, and a
 * caller that prints a sentence renders the date in that branch.
 */
fun dayLabel(
    day: LocalDay,
    now: Instant,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): DayLabel {
    val delta = day.epochDay - LocalDay.of(now, zone).epochDay
    return when {
        delta == 0L -> DayLabel.Today
        delta == -1L -> DayLabel.Yesterday
        delta in OLDEST_WEEKDAY..NEWEST_WEEKDAY -> DayLabel.Weekday(day)
        else -> DayLabel.Absolute(day)
    }
}

/**
 * The WORDS for a day label, which [dayLabel] deliberately does not decide.
 *
 * Here rather than in the separator that draws it, because "Today" and "Yesterday" are not the chat
 * screen's private vocabulary: the timeline's separators, the chat list's time column and Discover's
 * post bylines all print the same words, and the day one of them was localised and the others were
 * not is the day a Russian phone starts reading half-translated. One rule, one set of keys, every
 * caller.
 *
 * THE WEEKDAY CASE ARRIVED HERE RATHER THAN BESIDE IT, which is the whole reason this pass touched
 * three screens instead of one. The chat list's mockup asks for «Вчера», «Пн» and «Вс», and adding
 * that rule to the chat list alone would have put two date formats one tap apart -- a row saying
 * «Пн» opening onto a separator saying «03.08.2026». Extending [DayLabel] instead means the
 * separator and the Discover byline inherit the new form the moment it exists, and the surfaces that
 * must NOT have it say so with an argument rather than a rule of their own.
 *
 * THE ABSOLUTE CASE IS A VALUE AND NOT COPY. `LocalDay.render()` produces the digits, every language
 * prints the same ones, and `value_passthrough` is the narrow, named way a rendered value reaches a
 * `UiText` without `UiText.Raw` existing for a literal to escape through. When the date FORMAT gets
 * a locale of its own, it changes inside [render] and nothing here moves.
 */
fun DayLabel.dayLabelText(): UiText = when (this) {
    DayLabel.Today -> Res.string.chat_day_today.text()
    DayLabel.Yesterday -> Res.string.chat_day_yesterday.text()
    is DayLabel.Weekday -> day.dayOfWeek.shortNameKey().text()
    is DayLabel.Absolute -> Res.string.value_passthrough.text(day.render())
}

// ---------------------------------------------------------------------------------------------
// How old a POST is: the feed's byline. THE SAME RULE, WITH A DURATION HEAD ON IT.
// ---------------------------------------------------------------------------------------------

private const val SECONDS_PER_MINUTE = 60L
private const val SECONDS_PER_HOUR = 60L * 60L

/**
 * How long ago a post was written, as a structured value.
 *
 * A NEW FORM ON AN OLD RULE, WHICH IS THE WHOLE POINT OF THE SHAPE. The app has exactly one
 * relative-date rule -- [dayLabel] -> [DayLabel] -> [dayLabelText] -- and it answers "which DAY was
 * this", with four cases: today, yesterday, a weekday abbreviation, an explicit date. A feed wants
 * something that rule does not have: "2 hours", which is a DURATION and not a day at all.
 *
 * The wrong way to get it is a second date function beside the first, and this project has already
 * paid for that pattern twice ([dayLabel] records the second occasion). So [DayLabel] is not
 * replaced or copied here: it is the TAIL of this type. [Day] carries a whole [DayLabel], which
 * means every day older than today is worded by the same function the conversation's separators and
 * the chat list's time column use, and a post the feed calls «Пн» opens onto a byline that says «Пн»
 * because the same value produced both.
 *
 * IT WAS WRITTEN IN `ui/discover/DiscoverViewModel.kt` and moved here unchanged. That put half of
 * the app's relative-time vocabulary in a feed's view model, where a reader looking for the rules
 * would have found the other half and stopped -- and where the second surface to want a duration
 * would have had every reason to write its own.
 */
sealed interface FeedAge {

    /** Under a minute old, and the future. See [feedAge] on a clock that runs ahead. */
    data object Now : FeedAge

    data class Minutes(val minutes: Int) : FeedAge

    data class Hours(val hours: Int) : FeedAge

    /**
     * Not today: whatever [DayLabel] says, worded by [dayLabelText].
     *
     * [label] CAN NEVER BE [DayLabel.Today] by construction, and that invariant is the reason the
     * two halves cannot contradict each other. [feedAge] asks [dayLabel] FIRST and only falls
     * through to the duration head when the answer is today, so no post is ever describable both as
     * "5 hours" and as «Сегодня».
     */
    data class Day(val label: DayLabel) : FeedAge
}

/**
 * THE BOUNDARY IS A CALENDAR DAY, NOT TWENTY-FOUR HOURS, and that is the one real decision here.
 *
 * The obvious rule is "hours until 24 of them have passed, then a date", which is what most feeds
 * do. It is rejected, because it makes two surfaces disagree about one post. At 01:00 on Tuesday a
 * post written at 23:00 on Monday is two hours old AND was written yesterday; an elapsed-hours rule
 * puts «2 часа» in the feed and «Вчера 23:00» on the same post's detail screen, one tap apart.
 * Partitioning on the calendar day instead means the duration head and [DayLabel] answer about
 * disjoint sets of posts and can never describe the same one twice: that post reads «Вчера» in both
 * places.
 *
 * WHAT IT COSTS, stated rather than hidden: the largest duration this can print is 23 hours, and a
 * post from 23:00 last night reads «Вчера» at 01:00 rather than «2 часа». That is a real difference
 * from the mockup's likely intent and it is the price of one rule instead of two. It also means the
 * byline NEVER says «Сегодня»: a post from today is always a duration, which is strictly more
 * informative than the word.
 *
 * DST CANNOT SHIFT IT. [dayLabel] differences calendar days through `LocalDay.epochDay`, which has
 * no clock in it, so the two nights a year that are not 24 hours long move nothing here. An
 * elapsed-hours boundary would have had a 25-hour day print «Сегодня» for a post over 24 hours old.
 *
 * THE FUTURE IS [FeedAge.Now] WHEN IT IS STILL TODAY, and an explicit date otherwise. A device with
 * a skewed clock, or a peer whose clock runs ahead, produces a timestamp later than [now]; the
 * subtraction goes negative and falls into the first branch, which says "now" and is the least wrong
 * thing available. Past midnight it is [dayLabel]'s problem and [dayLabel] already answers it with a
 * date, deliberately, for reasons written at that function.
 *
 * Pure, so the thresholds are assertable without a renderer or a clock.
 */
fun feedAge(
    createdAt: Instant,
    now: Instant,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): FeedAge {
    val day = dayLabel(LocalDay.of(createdAt, zone), now, zone)
    if (day != DayLabel.Today) return FeedAge.Day(day)

    val seconds = now.epochSeconds - createdAt.epochSeconds
    if (seconds < SECONDS_PER_MINUTE) return FeedAge.Now
    // Bounded above by the calendar-day test, so both narrowings are safe. The largest either can
    // reach is 59 minutes and, on an ordinary day, 23 hours -- 24 on the one night a year that is
    // 25 hours long, which is a true statement about a post still dated today and needs no guard.
    if (seconds < SECONDS_PER_HOUR) return FeedAge.Minutes((seconds / SECONDS_PER_MINUTE).toInt())
    return FeedAge.Hours((seconds / SECONDS_PER_HOUR).toInt())
}

/**
 * The WORDS for a [FeedAge], which [feedAge] deliberately does not decide.
 *
 * The same split [dayLabelText] makes just above, and for the same reason: the rule is testable
 * without a locale and the copy is translatable without a rule.
 *
 * THE DAY CASE DELEGATES RATHER THAN DECIDING. [dayLabelText] owns «Сегодня», «Вчера», the seven
 * weekday abbreviations and the passthrough for an explicit date, and this reaches all four through
 * it. Declaring `feed_day_yesterday` would have been the whole failure this project keeps writing
 * down: two keys with the same English, passing every lint, diverging the first time one of them
 * was translated.
 *
 * The three duration forms are PLURALS, not `if (n == 1)`. Russian needs 1 час, 2 часа, 5 часов and
 * 21 час, and no comparison against 1 reaches any of them.
 */
fun FeedAge.feedAgeText(): UiText = when (this) {
    FeedAge.Now -> Res.string.feed_age_now.text()
    is FeedAge.Minutes -> Res.plurals.feed_age_minutes.count(minutes)
    is FeedAge.Hours -> Res.plurals.feed_age_hours.count(hours)
    is FeedAge.Day -> label.dayLabelText()
}

/**
 * The seven short weekday names, as KEYS.
 *
 * A `when` OVER THE ENUM AND NOT A LIST INDEXED BY `ordinal`, which is the whole reason this is
 * seven lines instead of one. An array lookup that is off by one shifts every day of the week at
 * once, and every label it produces is a real weekday name in the right shape -- nothing looks
 * broken, the app just quietly says Tuesday about Monday. This host has no device to catch that on.
 * The `when` cannot be off by one: each name is written against the day it belongs to, and a
 * missing one is a compile error rather than a wrong word.
 *
 * They are COPY and live in `strings_chat.xml` beside `chat_day_today` and `chat_day_yesterday`,
 * because that block is the app's whole vocabulary for naming a day and splitting it across two
 * files is how half of it ends up translated.
 */
private fun DayOfWeek.shortNameKey(): StringResource = when (this) {
    DayOfWeek.MONDAY -> Res.string.chat_day_mon
    DayOfWeek.TUESDAY -> Res.string.chat_day_tue
    DayOfWeek.WEDNESDAY -> Res.string.chat_day_wed
    DayOfWeek.THURSDAY -> Res.string.chat_day_thu
    DayOfWeek.FRIDAY -> Res.string.chat_day_fri
    DayOfWeek.SATURDAY -> Res.string.chat_day_sat
    DayOfWeek.SUNDAY -> Res.string.chat_day_sun
}

/** Fallback rendering for an absolute date, used until localized formatting lands. */
fun LocalDay.render(): String = "${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.$year"
